import React, {useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useKeycloak} from '@react-keycloak/web';

import 'shared/styles/table.style.less';
import {FcCheckmark} from 'react-icons/all';
import {Button, Col, Divider, Form, Input, message, Radio, Row} from 'antd';
import AppTableContainer from '@crema/core/AppTableContainer';
import {PractitionerContext} from './PractitionerContext';
import {findPractitioners} from 'services/PractitionerService';
import {handleError} from 'utils/MessageUtils';
import {X_TOTAL_COUNT} from 'shared/constants/AppConst';
import {EQUAL, IGNORE_CASE_LIKE} from 'shared/constants/QueryModes';

const PractitionerTable = () => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const navigate = useNavigate();
  const practitionerContext = useContext(PractitionerContext);
  const [form] = Form.useForm();

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const {reloading, setReloading} = practitionerContext;

  const [practitioners, setPractitioners] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    position: ['bottomCenter'],
  });
  const [loading, setLoading] = useState(false);
  const [searchValues, setSearchValues] = useState();

  const loadData = (params) => {
    const filters = params.filters
      ? Object.keys(params.filters).map((key) => ({
          fields: key,
          value: params.filters[key],
        }))
      : [];

    if (searchValues?.name) {
      filters.push({
        fields: 'name',
        mode: IGNORE_CASE_LIKE,
        value: searchValues.name,
      });
    }

    if (searchValues?.idn) {
      filters.push({
        fields: 'idn',
        mode: IGNORE_CASE_LIKE,
        value: searchValues.idn,
      });
    }

    if (searchValues?.ppn) {
      filters.push({
        fields: 'ppn',
        mode: IGNORE_CASE_LIKE,
        value: searchValues.ppn,
      });
    }

    if (searchValues?.phone) {
      filters.push({
        fields: 'phone',
        mode: IGNORE_CASE_LIKE,
        value: searchValues.phone,
      });
    }

    if (searchValues?.active) {
      filters.push({
        fields: 'active',
        mode: EQUAL,
        value: searchValues.active,
      });
    }

    if (searchValues?.gender) {
      filters.push({
        fields: 'gender',
        mode: EQUAL,
        value: searchValues.gender,
      });
    }

    setLoading(true);
    findPractitioners(headers, {
      pagination: params.pagination,
      filters,
      sorters: params.sorters,
    })
      .then((res) => {
        setPractitioners(res.data);

        setPagination({
          ...params.pagination,
          total: res.headers[X_TOTAL_COUNT]
            ? parseInt(res.headers[X_TOTAL_COUNT])
            : 0,
        });
      })
      .catch((error) => handleError(message, error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData({pagination});
  }, []);

  useEffect(() => {
    if (reloading) {
      loadData({pagination});
      setReloading(false);
    }
  }, [reloading]);

  useEffect(() => {
    if (searchValues) {
      loadData({
        pagination: {...pagination, current: 1, pageSize: 20},
      });
    }

    form.resetFields();
  }, [searchValues]);

  const onView = (id) => {
    navigate(`/health/practitioners/${id}`);
  };

  const onChange = (pagination, filters, sorter) => {
    loadData({pagination, filters, sorters: [sorter]});
  };

  const onSearch = (values) => {
    setSearchValues(values);
  };

  const onClearSearch = () => {
    setSearchValues({});
  };

  const columns = [
    {
      title: intl.messages['practitioner.name'],
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name, record) => (
        <Button type='link' onClick={() => onView(record.id)}>
          {name}
        </Button>
      ),
    },
    {
      title: intl.messages['common.enabled'],
      dataIndex: 'active',
      key: 'active',
      sorter: true,
      render: (active) => <span>{active ? <FcCheckmark /> : <></>}</span>,
    },
    {
      title: intl.messages['practitioner.idn'],
      dataIndex: 'idn',
      key: 'idn',
    },
    {
      title: intl.messages['practitioner.ppn'],
      dataIndex: 'ppn',
      key: 'ppn',
    },
    {
      title: intl.messages['common.phoneNumber'],
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: intl.messages['common.gender'],
      dataIndex: 'gender',
      key: 'gender',
      sorter: true,
      render: (gender) => (
        <span>
          {gender ? intl.messages[`common.gender.${gender.toLowerCase()}`] : ''}
        </span>
      ),
    },
    {
      title: intl.messages['common.modifiedDate'],
      dataIndex: 'modifiedDate',
      key: 'modifiedDate',
    },
  ];
  return (
    <>
      <div style={{padding: 10}}>
        <Form
          layout='vertical'
          form={form}
          initialValues={searchValues}
          onFinish={onSearch}>
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item name='name' label={intl.messages['practitioner.name']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name='idn' label={intl.messages['practitioner.idn']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name='ppn' label={intl.messages['practitioner.ppn']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name='phone'
                label={intl.messages['common.phoneNumber']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name='active' label={intl.messages['common.enabled']}>
                <Radio.Group>
                  <Radio value='true'>{intl.messages['common.yes']}</Radio>
                  <Radio value='false'>{intl.messages['common.no']}</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name='gender' label={intl.messages['common.gender']}>
                <Radio.Group>
                  <Radio value='MALE'>
                    {intl.messages['common.gender.male']}
                  </Radio>
                  <Radio value='FEMALE'>
                    {intl.messages['common.gender.female']}
                  </Radio>
                  <Radio value='OTHER'>
                    {intl.messages['common.gender.other']}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={10} />
            <Col span={2}>
              <Button onClick={onClearSearch} style={{width: '100%'}}>
                {intl.messages['common.reset']}
              </Button>
            </Col>
            <Col span={2}>
              <Button type='primary' htmlType='submit' style={{width: '100%'}}>
                {intl.messages['common.search']}
              </Button>
            </Col>
            <Col span={10} />
          </Row>
        </Form>
      </div>

      <Divider />

      <AppTableContainer
        className='customer-table'
        hoverColor
        data={practitioners}
        columns={columns}
        rowKey={(record) => record.id}
        loading={loading}
        pagination={pagination}
        onChange={onChange}
        scroll={{x: 'auto'}}
      />
    </>
  );
};

export default PractitionerTable;
